import type { ComponentChildren } from 'preact'
import { createContext } from 'preact'
import { useContext, useId } from 'preact/hooks'
import styles from './setting-row.module.css'

interface SettingIds {
  readonly id: string
  readonly labelId: string
  readonly describedBy?: string
}

const SettingIdsContext = createContext<SettingIds | null>(null)

/** The ids of the surrounding `SettingRow`, or fresh ones when there is no row. */
export function useSettingIds(): SettingIds {
  const fromRow = useContext(SettingIdsContext)
  const ownId = useId()
  return fromRow ?? { id: ownId, labelId: `${ownId}-label` }
}

interface SettingRowProps {
  readonly label: string
  readonly description?: string
  readonly children: ComponentChildren
}

/** A label, an optional description, and the control that edits the setting. */
export function SettingRow({ label, description, children }: SettingRowProps) {
  const id = useId()
  const describedBy = description === undefined ? undefined : `${id}-desc`
  const ids: SettingIds =
    describedBy === undefined
      ? { id, labelId: `${id}-label` }
      : { id, labelId: `${id}-label`, describedBy }

  return (
    <div class={styles.row}>
      <div class={styles.copy}>
        <label class={styles.label} id={ids.labelId} for={id}>
          {label}
        </label>
        {description !== undefined && (
          <p class={styles.description} id={describedBy}>
            {description}
          </p>
        )}
      </div>
      <SettingIdsContext.Provider value={ids}>
        <div class={styles.control}>{children}</div>
      </SettingIdsContext.Provider>
    </div>
  )
}
